import { db, SyncManager } from 'db';
import { useEffect } from 'react';
import { useForm } from 'react-final-form';
import { PostSyncUpdates, SyncListerFormResponse } from 'services/api/vehicle-sections';
import { Section } from 'services/api/vehicle-sections/types';
import { entityToPathFinderMap, findCheckboxPath, findImagePath } from '../utils';

type UpdateConfig = {
  section: boolean;
  checkboxes: boolean;
  images: boolean;
};

export const useUpdateFormAfterSync = (
  vehicleId: number,
  config: UpdateConfig = { section: true, checkboxes: true, images: true }
) => {
  const form = useForm();

  const updateNotesId = (
    values: Record<string, Section>,
    postSyncUpdates: PostSyncUpdates,
    entityType: 'image' | 'section' | 'checkbox'
  ) => {
    const findPath = entityToPathFinderMap[entityType];

    for (const key in postSyncUpdates) {
      const payload = postSyncUpdates[key];
      const path = findPath(values, Number(key));
      console.log(key, path, 'key + path');

      if (!path) {
        console.info(`Path not found for ${key} in ${entityType} entity`);
        continue;
      }

      if ('notesId' in payload) {
        form.change(`${path}.notes.id`, payload.notesId);
      }
      if ('url' in payload) {
        form.change(`${path}.url`, payload.url);
        form.change(`${path}.url_thumb`, payload.url_thumb);
      }
      if ('video' in payload) {
        form.change(`${path}.video`, payload.video);
      }
    }
  };

  const updateCheckboxIds = (
    values: Record<string, Section>,
    tmpToRealMap: SyncListerFormResponse['checkboxIdMap']
  ) => {
    for (const key in tmpToRealMap) {
      const { id: newId, notesId } = tmpToRealMap[key];
      const path = findCheckboxPath(values, key);
      if (!path) {
        console.info(`Could not find checkbox path for id ${key}. Real id: ${newId}`);
        continue;
      }
      form.change(`${path}.id`, newId);
      if (notesId) {
        form.change(`${path}.notes.id`, notesId);
      }
    }
  };

  const updateImageIds = (values: Record<string, Section>, tmpToRealMap: SyncListerFormResponse['imagesIdMap']) => {
    for (const key in tmpToRealMap) {
      const { id: newId, notesId, url, url_thumb } = tmpToRealMap[key];
      const path = findImagePath(values, key);
      const current = form.getFieldState(path!);
      console.log(newId, path, key, current?.value, 'newId + path + tmpId + current');
      if (!path) {
        console.info(`Could not find image path for id ${key}. Real id: ${newId}`);
        continue;
      }

      form.change(`${path}.id`, newId);
      form.change(`${path}.url`, url);
      form.change(`${path}.url_thumb`, url_thumb);
      if (notesId) {
        form.change(`${path}.notes.id`, notesId);
      }
    }
  };

  const updateUnsyncedQueue = async (tmpToRealMap: SyncListerFormResponse['imagesIdMap']) => {
    for (const key in tmpToRealMap) {
      const { id: newId } = tmpToRealMap[key];
      await db.entityUpdates
        .where('formId')
        .equals(vehicleId)
        .and(item => !item.synced && item.entityType === 'new_image' && item.entityId === key)
        .modify({ entityId: newId, entityType: 'image' });
      await db.removedImages
        .where('formId')
        .equals(vehicleId)
        .and(item => item.imageId.toString() === key)
        .modify({ imageId: newId });
    }
  };

  const updateUnsyncedCheckboxQueue = async (tmpToRealMap: SyncListerFormResponse['checkboxIdMap']) => {
    for (const key in tmpToRealMap) {
      const { id: newId } = tmpToRealMap[key];
      await db.entityUpdates
        .where('formId')
        .equals(vehicleId)
        .and(item => !item.synced && item.entityType === 'new_checkbox' && item.entityId === key)
        .modify({ entityId: newId, entityType: 'checkbox' });
    }
  };

  useEffect(() => {
    if (!vehicleId) return;
    const syncManager = SyncManager.getInstance(vehicleId);
    syncManager.onSyncFinished = async data => {
      const values = form.getState().values;
      if (config.section) {
        updateNotesId(values, data.notesEntityIdMap.section, 'section');
      }
      if (config.checkboxes) {
        updateNotesId(values, data.notesEntityIdMap.checkbox, 'checkbox');
        updateCheckboxIds(values, data.checkboxIdMap);
        await updateUnsyncedCheckboxQueue(data.checkboxIdMap);
      }
      if (config.images) {
        updateNotesId(values, data.notesEntityIdMap.image, 'image');
        updateImageIds(values, data.imagesIdMap);
        await updateUnsyncedQueue(data.imagesIdMap);
      }
    };
    return () => syncManager.stop();
  }, [vehicleId, config.checkboxes, config.images, config.section]);
};
